/**
 * Gmail OAuth Service
 *
 * Microservice for managing Gmail OAuth authentication
 * Handles Gmail account connection, token management, and authorization
 */

import { supabase } from './supabaseClient';

const GMAIL_SCOPES = [
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/userinfo.email'
].join(' ');

/**
 * Get Gmail OAuth URL for account linking
 * @param {string} userId - Current user's ID
 * @returns {Promise<string>} OAuth authorization URL
 */
export const getGmailAuthUrl = async (userId) => {
  try {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/gmail-callback`;

    // Store state for CSRF protection
    const state = btoa(JSON.stringify({
      userId,
      timestamp: Date.now(),
      nonce: crypto.randomUUID()
    }));

    localStorage.setItem('gmail_oauth_state', state);

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', GMAIL_SCOPES);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', state);

    return authUrl.toString();
  } catch (error) {
    console.error('Error generating Gmail auth URL:', error);
    throw error;
  }
};

/**
 * Exchange OAuth code for tokens and save Gmail account
 * @param {string} code - OAuth authorization code
 * @param {string} state - State parameter for CSRF validation
 * @returns {Promise<Object>} Gmail account data
 */
export const handleGmailCallback = async (code, state) => {
  try {
    // Validate state
    const storedState = localStorage.getItem('gmail_oauth_state');
    if (!storedState || storedState !== state) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    // Clear stored state
    localStorage.removeItem('gmail_oauth_state');

    // Ensure valid user session before calling Edge Function (ES256 JWT required)
    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (!authSession?.access_token) {
      throw new Error('Token exchange failed: No active user session');
    }

    // Exchange code for tokens via server-side Edge Function (client_secret stays server-side)
    const { data: tokenResponseData, error: tokenError } = await supabase.functions.invoke('gmail-oauth-exchange', {
      body: {
        grant_type: 'authorization_code',
        code,
        redirect_uri: `${window.location.origin}/gmail-callback`,
      }
    });

    if (tokenError || tokenResponseData?.error) {
      throw new Error(`Token exchange failed: ${tokenResponseData?.error || tokenError?.message}`);
    }

    const tokens = tokenResponseData;



    // Get Gmail user info
    const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`
      }
    });

    if (!userInfoResponse.ok) {
      throw new Error('Failed to fetch Gmail user info');
    }

    const userInfo = await userInfoResponse.json();

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Save Gmail account to database
    const { data: gmailAccount, error: dbError } = await supabase
      .from('synced_gmail_accounts')
      .upsert({
        user_id: user.id,
        gmail_email: userInfo.email,
        gmail_refresh_token: tokens.refresh_token,
        sync_status: 'pending',
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,gmail_email'
      })
      .select()
      .single();

    if (dbError) {
      console.error('Error saving Gmail account:', dbError);
      throw dbError;
    }

    return gmailAccount;
  } catch (error) {
    console.error('Error handling Gmail callback:', error);
    throw error;
  }
};

/**
 * Get access token for a Gmail account
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<string>} Access token
 */
export const getGmailAccessToken = async (refreshToken) => {
  try {
    // Debug: log what we're working with
    console.log('🔑 getGmailAccessToken called with:', {
      hasToken: !!refreshToken,
      tokenLength: refreshToken?.length,
      tokenPrefix: refreshToken ? refreshToken.substring(0, 10) + '...' : 'NULL'
    });

    // Ensure we have a valid user session before calling Edge Function
    // Without this, the Supabase client sends the HS256 anon key which fails ES256 JWKS verification
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Token refresh failed: No active user session');
    }

    // Refresh token via server-side Edge Function (client_secret stays server-side)
    const { data: tokenData, error: tokenError } = await supabase.functions.invoke('gmail-oauth-exchange', {
      body: {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }
    });

    if (tokenError || tokenData?.error) {
      console.error('❌ Token refresh failed:', tokenData?.error || tokenError?.message);
      console.error('   Full error response:', { tokenData, tokenError });
      throw new Error(`Token refresh failed: ${tokenData?.error || tokenError?.message}`);
    }

    console.log('✅ Access token obtained successfully');
    return tokenData.access_token;
  } catch (error) {
    console.error('💥 Exception in getGmailAccessToken:', error);
    console.error('   Error type:', error.constructor.name);
    console.error('   Error message:', error.message);
    throw error;
  }
};

/**
 * Get all synced Gmail accounts for current user
 * @returns {Promise<Array>} List of synced Gmail accounts
 */
export const getSyncedGmailAccounts = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('synced_gmail_accounts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching synced Gmail accounts:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('getSyncedGmailAccounts error:', error);
    throw error;
  }
};

/**
 * Remove a synced Gmail account and all its subscriptions
 * @param {string} gmailAccountId - Gmail account ID
 * @returns {Promise<void>}
 */
export const removeGmailAccount = async (gmailAccountId) => {
  try {
    // Get the Gmail account first to find associated subscriptions
    const { data: gmailAccount, error: fetchError } = await supabase
      .from('synced_gmail_accounts')
      .select('gmail_email')
      .eq('id', gmailAccountId)
      .single();

    if (fetchError) throw fetchError;

    // Delete all subscriptions imported from this Gmail
    const { error: subError } = await supabase
      .from('subscriptions')
      .delete()
      .eq('imported_from_email', gmailAccount.gmail_email);

    if (subError) {
      console.error('Error deleting Gmail subscriptions:', subError);
      throw subError;
    }

    // Delete the Gmail account record
    const { error: deleteError } = await supabase
      .from('synced_gmail_accounts')
      .delete()
      .eq('id', gmailAccountId);

    if (deleteError) {
      console.error('Error removing Gmail account:', deleteError);
      throw deleteError;
    }
  } catch (error) {
    console.error('removeGmailAccount error:', error);
    throw error;
  }
};

/**
 * Add Gmail account from signup OAuth session
 * Extracts Gmail tokens from Supabase session and creates synced_gmail_accounts record
 * @param {Object} session - Supabase auth session from signup
 * @returns {Promise<Object|null>} Created Gmail account record or null if no tokens
 */
export const addGmailAccountFromSession = async (session) => {
  try {
    console.log('🔍 DEBUG: Attempting to extract Gmail tokens from session...');

    if (!session?.user) {
      console.log('❌ No user in session');
      return null;
    }

    // Session token sources checked below — never log full session object (contains tokens)

    // Try to extract tokens from various locations
    let accessToken = null;
    let refreshToken = null;

    // Location 1: Direct session properties (most common for Supabase OAuth)
    if (session.provider_token) {
      accessToken = session.provider_token;
      console.log('✅ Found access token in session.provider_token');
    }
    if (session.provider_refresh_token) {
      refreshToken = session.provider_refresh_token;
      console.log('✅ Found refresh token in session.provider_refresh_token');
    }

    // Location 2: Check user identities (Google identity data)
    if (!accessToken || !refreshToken) {
      const googleIdentity = session.user?.identities?.find(id => id.provider === 'google');
      if (googleIdentity?.identity_data) {
        console.log('🔍 Checking Google identity data...');
        if (!accessToken && googleIdentity.identity_data.access_token) {
          accessToken = googleIdentity.identity_data.access_token;
          console.log('✅ Found access token in identity_data');
        }
        if (!refreshToken && googleIdentity.identity_data.refresh_token) {
          refreshToken = googleIdentity.identity_data.refresh_token;
          console.log('✅ Found refresh token in identity_data');
        }
      }
    }

    // Location 3: Check user metadata
    if (!accessToken && session.user?.user_metadata?.provider_token) {
      accessToken = session.user.user_metadata.provider_token;
      console.log('✅ Found access token in user_metadata');
    }
    if (!refreshToken && session.user?.user_metadata?.provider_refresh_token) {
      refreshToken = session.user.user_metadata.provider_refresh_token;
      console.log('✅ Found refresh token in user_metadata');
    }

    // Check if we have at minimum the access token
    if (!accessToken) {
      console.log('⚠️ No access token found — user may not have granted Gmail permissions');
      return null;
    }

    // If we have access token but no refresh token, we can still proceed.
    // The access token proves the user granted Gmail access during signup.
    // The refresh token will be obtained through the onboarding Gmail popup flow.
    if (!refreshToken) {
      console.log('ℹ️ Access token found but no refresh token — will save account and get refresh token during onboarding');
    }

    console.log(`✅ Token extraction complete — access: ✓, refresh: ${refreshToken ? '✓' : '✗ (will get during onboarding)'}`);

    // Verify that the token actually has Gmail scope by making a test API call
    console.log('🔐 Verifying Gmail API access...');
    const gmailTestResponse = await fetch(
      'https://gmail.googleapis.com/gmail/v1/users/me/profile',
      { headers: { 'Authorization': `Bearer ${accessToken}` } }
    );

    if (!gmailTestResponse.ok) {
      const errorStatus = gmailTestResponse.status;
      console.error(`❌ Gmail API test failed with status ${errorStatus}`);

      if (errorStatus === 403) {
        console.error('⚠️ USER DID NOT GRANT GMAIL PERMISSION!');
        console.error('   The user unchecked the Gmail checkbox during Google OAuth.');
        console.error('   They need to re-authenticate and check "View your email messages and settings"');

        // Store a flag so the UI can show a warning
        localStorage.setItem('gmail_scope_missing', 'true');

        // Return a special object to indicate scope issue
        return { scopeMissing: true, email: session.user.email };
      }

      console.error('Gmail API verification failed:', await gmailTestResponse.text());
      return null;
    }

    console.log('✅ Gmail API access verified - scope is correct');
    // Clear any previous scope warning
    localStorage.removeItem('gmail_scope_missing');

    // Get Gmail user info using the access token
    console.log('📧 Fetching Gmail user info...');
    const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!userInfoResponse.ok) {
      console.error('❌ Failed to fetch Gmail user info:', userInfoResponse.status, userInfoResponse.statusText);
      return null;
    }

    const userInfo = await userInfoResponse.json();
    console.log('✅ Gmail user info retrieved:', userInfo.email);

    // Check if this Gmail account is already synced
    const { data: existing } = await supabase
      .from('synced_gmail_accounts')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('gmail_email', userInfo.email)
      .maybeSingle();

    if (existing) {
      console.log('ℹ️ Gmail account already exists:', userInfo.email);

      // Build update payload — only include refresh token if we actually got a new one.
      // Returning users (prompt:'select_account') won't have a provider_refresh_token,
      // so we must NOT overwrite the valid token already stored in the DB.
      const updatePayload = {
        sync_status: 'pending',
        updated_at: new Date().toISOString()
      };
      if (refreshToken) {
        updatePayload.gmail_refresh_token = refreshToken;
        console.log('✅ Updating refresh token (new one provided by session)');
      } else {
        console.log('ℹ️ No new refresh token in session — keeping existing token in DB');
      }

      const { data: updated, error: updateError } = await supabase
        .from('synced_gmail_accounts')
        .update(updatePayload)
        .eq('id', existing.id)
        .select()
        .single();

      if (updateError) {
        console.error('❌ Error updating Gmail account:', updateError);
        throw updateError;
      }

      console.log('✅ Gmail account refresh token updated:', userInfo.email);
      return updated;
    }

    // Create or update Gmail account in synced_gmail_accounts
    // If the account already exists (race condition with SIGNED_IN firing twice),
    // the upsert on user_id+gmail_email will update it. We must NOT wipe
    // a valid refresh token with null in that case.
    console.log('💾 Creating/upserting synced_gmail_accounts record...');

    const upsertData = {
      user_id: session.user.id,
      gmail_email: userInfo.email,
      sync_status: 'pending',
      updated_at: new Date().toISOString()
    };

    // Only include refresh token if we actually have one.
    // If we don't, the upsert might match an existing row (via onConflict),
    // and we must NOT overwrite its valid token with null.
    if (refreshToken) {
      upsertData.gmail_refresh_token = refreshToken;
      console.log('✅ Including refresh token in upsert');
    } else {
      console.log('ℹ️ No refresh token available — upsert will NOT overwrite existing token');
    }

    const { data: gmailAccount, error: dbError } = await supabase
      .from('synced_gmail_accounts')
      .upsert(upsertData, {
        onConflict: 'user_id,gmail_email',
        ignoreDuplicates: false
      })
      .select()
      .single();

    if (dbError) {
      console.error('❌ Error saving Gmail account from session:', dbError);
      throw dbError;
    }

    console.log('✅ Gmail account auto-added from signup:', userInfo.email);
    return gmailAccount;
  } catch (error) {
    console.error('💥 addGmailAccountFromSession error:', error);
    console.error('   Error type:', error.constructor.name);
    console.error('   Error message:', error.message);
    // Return null instead of throwing to not break signup flow
    return null;
  }
};

export default {
  getGmailAuthUrl,
  handleGmailCallback,
  addGmailAccountFromSession,
  getGmailAccessToken,
  getSyncedGmailAccounts,
  removeGmailAccount
};
